/**
 * Scherm 04 — Dashboard: waar staat elk bouwelement, op welke kar ligt het werk en
 * hoeveel uren zitten erin.
 *
 * We werken hier met karren en bouwelementen, niet met losse onderdelen. Een rij is
 * uit te vouwen naar de karren eronder. Rode waarden komen hier niet voor — die staan
 * uitsluitend in de foutcontrole van scherm 02. Voortgang per boot zit er ook niet in:
 * dat wordt in PowerBI bijgehouden.
 *
 * Niet toegankelijk voor productie en tekenaars; de API weigert dit endpoint voor die
 * rollen, dus dit scherm wordt voor hen ook niet in de navigatie gezet.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, type Dashboard as DashboardData, type DashboardKarrij } from '../api';
import { bouwnummerKort, datumTijd, procent, uren, useGegevens } from '../gegevens';
import { Maatlijn } from '../onderdelen/Maatlijn';
import { Bouwnummerfilter } from '../onderdelen/Bouwnummerfilter';
import { IconChevronRechts, IconHerhaal } from '../onderdelen/Iconen';

export function Dashboard() {
  const { boot, kleuren, meldFout, wijzigingsteller } = useGegevens();
  const [data, setData] = useState<DashboardData | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [laden, setLaden] = useState(false);

  const bouwnummer = boot?.bouwnummer;

  const haal = useCallback(async () => {
    if (!bouwnummer) return;
    setLaden(true);
    try {
      setData(await api.dashboard(bouwnummer));
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'Het dashboard kon niet geladen worden.');
    } finally {
      setLaden(false);
    }
  }, [bouwnummer, meldFout]);

  useEffect(() => {
    void haal();
  }, [haal, wijzigingsteller]);

  if (!boot) return <div className="hs-leeg">Kies eerst een boot.</div>;
  if (!data) return <div className="hs-leeg">{laden ? 'Het dashboard wordt geladen…' : ''}</div>;

  const s = data.samenvatting;

  return (
    <>
      <header className="hs-kop">
        <div className="hs-koprij">
          <div>
            <p className="hs-annotatie hs-kop__gebied">Inzicht</p>
            <h1 className="hs-h1">Dashboard</h1>
            <Maatlijn />
            <p className="hs-subregel">
              bouwnummer {bouwnummerKort(boot.bouwnummer)} · {s.karren}{' '}
              {s.karren === 1 ? 'kar' : 'karren'} met een houtstaat · {s.bouwelementen} bouwelementen
              {s.karrenZonderHoutstaat > 0
                ? ` · ${s.karrenZonderHoutstaat} karren uit de begroting wachten nog op de TopSolid-import`
                : ''}
            </p>
            <Bouwnummerfilter />
          </div>
          <div className="hs-kopacties">
            <button type="button" className="hs-knop" onClick={() => void haal()} disabled={laden}>
              <IconHerhaal /> Verversen
            </button>
          </div>
        </div>
      </header>

      <div className="hs-scherm">
        {/* KPI-rij. */}
        <div className="hs-kpirij">
          <div className="hs-paneel hs-kpi">
            <span className="hs-kpi__label">Gereed</span>
            <div className="hs-cijfer">{procent(s.gereedDeel)}</div>
            <div className="hs-balk">
              <div className="hs-balk__vulling" style={{ width: `${s.gereedDeel * 100}%` }} />
            </div>
          </div>
          <div className="hs-paneel hs-kpi">
            <span className="hs-kpi__label">Uren gemaakt</span>
            <div className="hs-cijfer">{uren(s.urenGemaakt)}</div>
            <span className="hs-muted">waarvan {uren(s.urenRework)} rework</span>
          </div>
          <div className="hs-paneel hs-kpi">
            <span className="hs-kpi__label">Uren resterend</span>
            <div className="hs-cijfer">{uren(s.urenResterend)}</div>
            <span className="hs-muted">volgens de normtijden</span>
          </div>
          <div className="hs-paneel hs-kpi">
            <span className="hs-kpi__label">In rework</span>
            <div className="hs-cijfer">{s.karrenInRework}</div>
            <span className="hs-muted">
              {s.karrenInRework === 1 ? 'kar' : 'karren'} · registratie blijft staan
            </span>
          </div>
          <div className={`hs-paneel hs-kpi${s.karrenOnHold > 0 ? ' hs-kpi--hold' : ''}`}>
            <span className="hs-kpi__label">On-hold</span>
            <div className="hs-cijfer">{s.karrenOnHold}</div>
            {s.onHold.length > 0 ? (
              <span className="hs-muted" style={{ color: 'var(--hold-tekst)' }}>
                {s.onHold[0]?.naam} · {s.onHold[0]?.door} · {datumTijd(s.onHold[0]?.op)} ·{' '}
                {s.onHold[0]?.reden}
              </span>
            ) : (
              <span className="hs-muted">niets geblokkeerd</span>
            )}
          </div>
        </div>

        {/* Belasting per afdeling — één samengevoegd overzicht. */}
        <section className="hs-paneel">
          <div className="hs-paneel__kop">
            <span className="hs-sectiekop">Belasting per afdeling</span>
            <span className="hs-muted">
              elke kleur is een afdeling of machine · resterend werk tegen de dagcapaciteit
            </span>
          </div>
          <div className="hs-belasting">
            {data.belasting.map((b) => (
              <div key={b.afdeling} className="hs-belasting__kolom">
                <div className="hs-belasting__streep" style={{ background: b.kleur }} />
                <span className="hs-kpi__label">{b.afdeling}</span>
                <div className="hs-cijfer hs-cijfer--klein">
                  {b.karren}
                  <span className="hs-muted" style={{ fontSize: 11, fontWeight: 400 }}>
                    {' '}
                    · {b.stuks} stuks
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 6,
                    fontSize: 11,
                    marginBottom: 4,
                  }}
                >
                  <span className="hs-mono">{uren(b.resterendeUren)}</span>
                  <span className="hs-mono hs-muted">
                    {String(b.doorlooptijdDagen).replace('.', ',')} d
                  </span>
                </div>
                <div className="hs-balk">
                  <div
                    className="hs-balk__vulling"
                    style={{ width: `${b.bezettingsgraad * 100}%`, background: b.kleur }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Bouwelementen, uit te vouwen naar de karren. */}
        <section className="hs-paneel">
          <div className="hs-paneel__kop">
            <span className="hs-sectiekop">Karren</span>
            <span className="hs-muted">klap een rij open voor de bouwelementen die erop staan</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="hs-tabel hs-bouwelementen">
              <colgroup>
                <col className="vouw" />
                <col className="element" />
                <col className="ruimte" />
                <col className="karren" />
                <col className="waar" />
                <col className="gereed" />
                <col className="uren" />
                <col className="uren" />
                <col className="uren" />
                <col className="verwacht" />
              </colgroup>
              <thead>
                <tr>
                  <th />
                  <th>Kar</th>
                  <th>Ruimte</th>
                  <th>Elementen</th>
                  <th>Route</th>
                  <th>Gereed</th>
                  <th className="hs-rechts">Gemaakt</th>
                  <th className="hs-rechts">Rework</th>
                  <th className="hs-rechts">Resterend</th>
                  <th>Verwacht</th>
                </tr>
              </thead>
              <tbody>
                {data.karren.map((element) => (
                  <Karrij
                    key={element.id}
                    kar={element}
                    open={open.has(element.id)}
                    reworkKleur={kleuren?.rework}
                    onWissel={() =>
                      setOpen((vorig) => {
                        const nieuw = new Set(vorig);
                        if (nieuw.has(element.id)) nieuw.delete(element.id);
                        else nieuw.add(element.id);
                        return nieuw;
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="hs-legenda">
            Rode waarden staan hier niet: die horen in de foutcontrole van de houtstaat. Voortgang per
            boot wordt in PowerBI bijgehouden.
          </div>
        </section>
      </div>
    </>
  );
}

/**
 * Eén regel op het dashboard: een kar. Uitklappen toont de bouwelementen die erop
 * staan — dat is wat er fysiek op die kar ligt.
 */
function Karrij({
  kar: element,
  open,
  reworkKleur,
  onWissel,
}: {
  kar: DashboardKarrij;
  open: boolean;
  reworkKleur?: { kleur: string; tekst: string; rand: string };
  onWissel: () => void;
}) {
  const onHold = element.status === 'on-hold';

  return (
    <>
      {/*
       * De hele regel klapt uit, niet alleen het pijltje: dat pijltje is een klein doel
       * en de regel is waar de gebruiker toch al kijkt. De knop blijft staan voor het
       * toetsenbord en voor de schermlezer.
       */}
      <tr
        className={`hs-rij--klikbaar${onHold ? ' hs-rij--hold' : ''}`}
        onClick={onWissel}
        title={open ? 'Karren verbergen' : 'Karren tonen'}
      >
        <td>
          <button
            type="button"
            className={`hs-vouwknop${open ? ' open' : ''}`}
            // De rij vangt de klik al af; anders klapt hij twee keer om.
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
            aria-expanded={open}
            aria-label={open ? 'Karren verbergen' : 'Karren tonen'}
          >
            <IconChevronRechts maat={12} />
          </button>
        </td>
        <td>
          <span className="hs-mono" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {element.id}
          </span>{' '}
          <span className="hs-muted">{element.karNaam}</span>
          <span className="hs-mono hs-muted-licht" style={{ marginLeft: 6 }}>
            kar {element.kar}
          </span>
          {/* Bij de naam een korte markering; wie, wanneer en waarom staat in de
              kolom "waar ligt het werk" — daar is ruimte voor en daar kijkt de
              voorbereider toch al. */}
          {onHold ? (
            <span className="hs-chip hs-chip--hold" style={{ marginLeft: 6 }}>
              On-hold
            </span>
          ) : null}
          {element.reworkActief ? (
            <span
              className="hs-chip"
              style={{
                marginLeft: 6,
                background: reworkKleur?.kleur,
                borderColor: reworkKleur?.rand,
                color: reworkKleur?.tekst,
              }}
              title={element.reworkActief.reden}
            >
              Rework · {element.reworkActief.aangevraagdDoor}
            </span>
          ) : element.reworks > 0 ? (
            <span
              className="hs-chip"
              style={{ marginLeft: 6 }}
              title="Afgeronde rework, blijft geregistreerd"
            >
              {element.reworks}× rework gehad
            </span>
          ) : null}
        </td>
        <td className="hs-muted">
          {[element.deck, element.ruimte].filter(Boolean).join(' · ')}
        </td>
        <td className="hs-mono">
          {element.bouwelementen.length} · {element.onderdelen} onderdelen
        </td>
        <td>
          {onHold ? (
            <span style={{ color: 'var(--hold-tekst)', display: 'block', whiteSpace: 'normal' }}>
              geblokkeerd in planning
              <span style={{ display: 'block', fontSize: 11, lineHeight: 1.4 }}>
                {element.onHold?.door} · {datumTijd(element.onHold?.op)} · {element.onHold?.reden}
              </span>
            </span>
          ) : (
            <span
              className="hs-strip"
              title={element.stappen
                .map((s) => `${s.kolomnaam}${s.gereed ? ' ✓' : s.nu ? ' ← nu' : ''}`)
                .join(' · ')}
            >
              {element.stappen.map((stap) => (
                <span
                  key={stap.sleutel}
                  className="hs-strip__deel"
                  style={{
                    width: `${100 / Math.max(1, element.stappen.length)}%`,
                    background: stap.kleur,
                    // Wat gereed is dempt weg; waar de kar nu staat krijgt een rand.
                    opacity: stap.gereed ? 0.45 : 1,
                    boxShadow: stap.nu ? 'inset 0 0 0 2px var(--primair)' : undefined,
                  }}
                />
              ))}
            </span>
          )}
        </td>
        <td>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span className="hs-balk" style={{ width: 78 }}>
              <span
                className="hs-balk__vulling"
                style={{ width: `${element.voortgang * 100}%`, display: 'block' }}
              />
            </span>
            <span className="hs-mono" style={{ color: element.voortgang >= 1 ? 'var(--goed-tekst)' : undefined }}>
              {procent(element.voortgang)}
            </span>
          </span>
        </td>
        <td className="hs-mono hs-rechts">{uren(element.uren.gemaakt)}</td>
        <td className="hs-mono hs-rechts" style={{ color: element.uren.rework ? 'var(--hold-tekst)' : undefined }}>
          {element.uren.rework ? uren(element.uren.rework) : '—'}
        </td>
        <td className="hs-mono hs-rechts">{uren(element.uren.resterend)}</td>
        <td className="hs-mono hs-muted">{element.verwachtGereed ?? '—'}</td>
      </tr>

      {open
        ? element.bouwelementen.map((be) => (
            <tr key={be.id} className="hs-karregel">
              <td />
              <td>
                <span className="hs-mono" style={{ paddingLeft: 10, fontWeight: 600 }}>
                  {be.code}
                </span>{' '}
                <span>{be.naam}</span>
              </td>
              <td className="hs-muted">{be.onderdelen} onderdelen</td>
              <td className="hs-mono">{be.id}</td>
              <td colSpan={6} />
            </tr>
          ))
        : null}
    </>
  );
}
